import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../utils/formatters';
import { Loader2, PlusCircle, Trash2, Target, Edit3 } from 'lucide-react';

const Planner = ({ summary }) => {

  const [budgets, setBudgets] = useState([]);

  const [goals, setGoals] = useState([]);

  const [loading, setLoading] = useState(true);

  const [budgetForm, setBudgetForm] = useState({ category: '', limitAmount: '', period: 'monthly' });

  const [goalForm, setGoalForm] = useState({ name: '', targetAmount: '', targetDate: '', notes: '' });

  const [editingBudgetId, setEditingBudgetId] = useState(null);

  const [editingBudgetValues, setEditingBudgetValues] = useState({ category: '', limitAmount: '', period: 'monthly' });

  const [contributionForms, setContributionForms] = useState({});

  const [plannerError, setPlannerError] = useState(null);



  const actualSpendMap = useMemo(() => {

    const map = {};

    (summary?.spendByCategory || []).forEach((item) => {

      map[item.name] = item.value;

    });

    return map;

  }, [summary]);



  const fetchPlannerData = useCallback(async () => {

    setLoading(true);

    setPlannerError(null);

    try {

      const [budgetsRes, goalsRes] = await Promise.all([axios.get('/api/budgets'), axios.get('/api/goals')]);

      setBudgets(budgetsRes.data);

      setGoals(goalsRes.data);

    } catch (err) {

      console.error('Failed to load planner data:', err);

      setPlannerError('Failed to load planner data. Please try again.');

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    fetchPlannerData();

  }, [fetchPlannerData]);



  const handleBudgetFormChange = (field, value) => {

    setBudgetForm((prev) => ({ ...prev, [field]: value }));

  };



  const handleGoalFormChange = (field, value) => {

    setGoalForm((prev) => ({ ...prev, [field]: value }));

  };



  const handleBudgetSubmit = async (e) => {

    e.preventDefault();

    setPlannerError(null);

    try {

      await axios.post('/api/budgets', {

        category: budgetForm.category,

        limitAmount: Number(budgetForm.limitAmount),

        period: budgetForm.period,

      });

      setBudgetForm({ category: '', limitAmount: '', period: 'monthly' });

      fetchPlannerData();

    } catch (err) {

      console.error(err);

      const message = err.response?.data?.error || 'Failed to create budget.';

      setPlannerError(message);

    }

  };



  const startBudgetEdit = (budget) => {

    setEditingBudgetId(budget.id);

    setEditingBudgetValues({

      category: budget.category,

      limitAmount: budget.limitAmount,

      period: budget.period || 'monthly',

    });

  };



  const handleBudgetEditChange = (field, value) => {

    setEditingBudgetValues((prev) => ({ ...prev, [field]: value }));

  };



  const handleBudgetUpdate = async (budgetId) => {

    setPlannerError(null);

    try {

      await axios.put(`/api/budgets/${budgetId}`, {

        category: editingBudgetValues.category,

        limitAmount: Number(editingBudgetValues.limitAmount),

        period: editingBudgetValues.period,

      });

      setEditingBudgetId(null);

      fetchPlannerData();

    } catch (err) {

      console.error(err);

      const message = err.response?.data?.error || 'Failed to update budget.';

      setPlannerError(message);

    }

  };



  const handleBudgetDelete = async (budgetId) => {

    setPlannerError(null);

    try {

      await axios.delete(`/api/budgets/${budgetId}`);

      if (editingBudgetId === budgetId) {

        setEditingBudgetId(null);

      }

      fetchPlannerData();

    } catch (err) {

      console.error(err);

      const message = err.response?.data?.error || 'Failed to delete budget.';

      setPlannerError(message);

    }

  };



  const handleGoalSubmit = async (e) => {

    e.preventDefault();

    setPlannerError(null);

    try {

      await axios.post('/api/goals', {

        name: goalForm.name,

        targetAmount: Number(goalForm.targetAmount),

        targetDate: goalForm.targetDate || null,

        notes: goalForm.notes || null,

      });

      setGoalForm({ name: '', targetAmount: '', targetDate: '', notes: '' });

      fetchPlannerData();

    } catch (err) {

      console.error(err);

      const message = err.response?.data?.error || 'Failed to create goal.';

      setPlannerError(message);

    }

  };



  const handleGoalDelete = async (goalId) => {

    setPlannerError(null);

    try {

      await axios.delete(`/api/goals/${goalId}`);

      fetchPlannerData();

    } catch (err) {

      console.error(err);

      const message = err.response?.data?.error || 'Failed to delete goal.';

      setPlannerError(message);

    }

  };



  const handleContributionChange = (goalId, field, value) => {

    setContributionForms((prev) => ({

      ...prev,

      [goalId]: {

        ...(prev[goalId] || { amount: '', description: '' }),

        [field]: value,

      },

    }));

  };



  const handleContributionSubmit = async (goalId) => {

    const form = contributionForms[goalId] || {};

    const numericAmount = Number(form.amount);

    if (Number.isNaN(numericAmount) || numericAmount <= 0) {

      setPlannerError('Contribution amount must be greater than zero.');

      return;

    }

    setPlannerError(null);

    try {

      await axios.post(`/api/goals/${goalId}/contributions`, {

        amount: numericAmount,

        description: form.description || '',

      });

      setContributionForms((prev) => ({ ...prev, [goalId]: { amount: '', description: '' } }));

      fetchPlannerData();

    } catch (err) {

      console.error(err);

      const message = err.response?.data?.error || 'Failed to add contribution.';

      setPlannerError(message);

    }

  };



  return (

    <div className="container mx-auto p-8 space-y-10">

      <section className="bg-white p-6 rounded-lg shadow-xl border border-gray-200">

        <div className="flex items-center justify-between mb-6">

          <div>

            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">

              <DollarSign className="text-indigo-500" /> Budget Manager

            </h2>

            <p className="text-sm text-gray-500">Adjust category limits and keep your spending plan updated.</p>

          </div>

          <PlusCircle size={28} className="text-indigo-500" />

        </div>



        <form className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" onSubmit={handleBudgetSubmit}>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Category</label>

            <input

              type="text"

              required

              value={budgetForm.category}

              onChange={(e) => handleBudgetFormChange('category', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="e.g., Groceries"

            />

          </div>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Monthly Limit</label>

            <input

              type="number"

              min="0"

              step="0.01"

              required

              value={budgetForm.limitAmount}

              onChange={(e) => handleBudgetFormChange('limitAmount', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="500"

            />

          </div>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Period</label>

            <select

              value={budgetForm.period}

              onChange={(e) => handleBudgetFormChange('period', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

            >

              <option value="monthly">Monthly</option>

              <option value="quarterly">Quarterly</option>

              <option value="yearly">Yearly</option>

            </select>

          </div>

          <div className="flex items-end">

            <button

              type="submit"

              className="w-full bg-indigo-600 text-white py-2 rounded-md text-sm font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"

            >

              Add Budget

            </button>

          </div>

        </form>



        {loading ? (

          <div className="flex justify-center py-8">

            <Loader2 className="animate-spin text-indigo-600" size={32} />

          </div>

        ) : budgets.length === 0 ? (

          <p className="text-sm text-gray-500">No budgets yet. Add your first category above.</p>

        ) : (

          <div className="overflow-x-auto">

            <table className="min-w-full divide-y divide-gray-200 text-sm">

              <thead className="bg-gray-50">

                <tr>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Category</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Limit</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actual</th>

                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>

                  <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">Actions</th>

                </tr>

              </thead>

              <tbody className="divide-y divide-gray-100">

                {budgets.map((budget) => {

                  const actual = actualSpendMap[budget.category] || 0;

                  const percent = budget.limitAmount > 0 ? Math.min(100, (actual / budget.limitAmount) * 100) : 0;

                  const progressColor =

                    percent >= 100 ? 'bg-red-500' : percent >= 80 ? 'bg-yellow-500' : 'bg-green-500';

                  const isEditing = editingBudgetId === budget.id;



                  return (

                    <tr key={budget.id}>

                      <td className="px-4 py-3 font-medium text-gray-800">

                        {isEditing ? (

                          <input

                            type="text"

                            value={editingBudgetValues.category}

                            onChange={(e) => handleBudgetEditChange('category', e.target.value)}

                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                          />

                        ) : (

                          budget.category

                        )}

                      </td>

                      <td className="px-4 py-3 text-gray-700">

                        {isEditing ? (

                          <input

                            type="number"

                            min="0"

                            step="0.01"

                            value={editingBudgetValues.limitAmount}

                            onChange={(e) => handleBudgetEditChange('limitAmount', e.target.value)}

                            className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                          />

                        ) : (

                          formatCurrency(budget.limitAmount, 0)

                        )}

                      </td>

                      <td className="px-4 py-3 text-gray-700">{formatCurrency(actual, 0)}</td>

                      <td className="px-4 py-3 text-gray-700">

                        <div className="space-y-1">

                          <div className="w-full bg-gray-200 rounded-full h-2">

                            <div className={`${progressColor} h-2 rounded-full`} style={{ width: `${percent}%` }} />

                          </div>

                          <span className="text-xs text-gray-500">{percent.toFixed(1)}% used</span>

                        </div>

                      </td>

                      <td className="px-4 py-3 text-right space-x-2">

                        {isEditing ? (

                          <>

                            <button

                              onClick={() => handleBudgetUpdate(budget.id)}

                              className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-green-100 text-green-700 hover:bg-green-200"

                            >

                              Save

                            </button>

                            <button

                              onClick={() => setEditingBudgetId(null)}

                              className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"

                            >

                              Cancel

                            </button>

                          </>

                        ) : (

                          <>

                            <button

                              onClick={() => startBudgetEdit(budget)}

                              className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-100"

                            >

                              <Edit3 size={14} className="mr-1" />

                              Edit

                            </button>

                            <button

                              onClick={() => handleBudgetDelete(budget.id)}

                              className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md bg-red-50 text-red-600 hover:bg-red-100"

                            >

                              <Trash2 size={14} className="mr-1" />

                              Delete

                            </button>

                          </>

                        )}

                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </table>

          </div>

        )}

      </section>



      <section className="bg-white p-6 rounded-lg shadow-xl border border-gray-200">

        <div className="flex items-center justify-between mb-6">

          <div>

            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">

              <Target className="text-green-500" /> Savings Goals

            </h2>

            <p className="text-sm text-gray-500">Track progress towards savings milestones or debt pay-down targets.</p>

          </div>

        </div>



        <form className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" onSubmit={handleGoalSubmit}>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Goal Name</label>

            <input

              type="text"

              required

              value={goalForm.name}

              onChange={(e) => handleGoalFormChange('name', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="Emergency Fund"

            />

          </div>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Target Amount</label>

            <input

              type="number"

              min="0"

              step="0.01"

              required

              value={goalForm.targetAmount}

              onChange={(e) => handleGoalFormChange('targetAmount', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="5000"

            />

          </div>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Target Date</label>

            <input

              type="date"

              value={goalForm.targetDate}

              onChange={(e) => handleGoalFormChange('targetDate', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

            />

          </div>

          <div>

            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">Notes</label>

            <input

              type="text"

              value={goalForm.notes}

              onChange={(e) => handleGoalFormChange('notes', e.target.value)}

              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="Optional context"

            />

          </div>

          <div className="md:col-span-4 flex justify-end">

            <button

              type="submit"

              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"

            >

              Create Goal

            </button>

          </div>

        </form>



        {loading ? (

          <div className="flex justify-center py-8">

            <Loader2 className="animate-spin text-indigo-600" size={32} />

          </div>

        ) : goals.length === 0 ? (

          <p className="text-sm text-gray-500">No savings goals yet. Add one using the form above.</p>

        ) : (

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {goals.map((goal) => (

              <div key={goal.id} className="border border-gray-200 rounded-lg p-4 shadow-sm">

                <div className="flex items-center justify-between mb-2">

                  <div>

                    <h3 className="text-lg font-semibold text-gray-800">{goal.name}</h3>

                    <p className="text-xs text-gray-500">

                      Target: {formatCurrency(goal.targetAmount, 0)}{' '}

                      {goal.targetDate && <span className="ml-2">by {new Date(goal.targetDate).toLocaleDateString()}</span>}

                    </p>

                  </div>

                  <button

                    onClick={() => handleGoalDelete(goal.id)}

                    className="text-red-500 hover:text-red-700"

                    aria-label={`Delete ${goal.name}`}

                  >

                    <Trash2 size={18} />

                  </button>

                </div>

                {goal.notes && <p className="text-sm text-gray-600 mb-2">{goal.notes}</p>}



                <div className="mb-3">

                  <div className="flex justify-between text-sm text-gray-600">

                    <span>{formatCurrency(goal.currentAmount, 0)} saved</span>

                    <span>{goal.progressPercent}%</span>

                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">

                    <div

                      className="bg-green-500 h-2 rounded-full transition-all"

                      style={{ width: `${goal.progressPercent}%` }}

                    />

                  </div>

                </div>



                <div className="text-xs text-gray-500 mb-3">

                  Contributions logged: {goal.contributionCount}

                </div>



                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">

                  <input

                    type="number"

                    min="0"

                    step="0.01"

                    placeholder="Amount"

                    value={contributionForms[goal.id]?.amount || ''}

                    onChange={(e) => handleContributionChange(goal.id, 'amount', e.target.value)}

                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

                  />

                  <input

                    type="text"

                    placeholder="Note"

                    value={contributionForms[goal.id]?.description || ''}

                    onChange={(e) => handleContributionChange(goal.id, 'description', e.target.value)}

                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 md:col-span-2"

                  />

                </div>

                <div className="flex justify-end mt-3">

                  <button

                    onClick={() => handleContributionSubmit(goal.id)}

                    className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"

                  >

                    Add Contribution

                  </button>

                </div>

              </div>

            ))}

          </div>

        )}

      </section>



      {plannerError && (

        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">

          {plannerError}

        </div>

      )}

    </div>

  );

};




export default Planner;
